/**
 * PlusMyReports — signed-in-only list of the caller's past WUA
 * Personalised Reports. Consumes `GET /api/wua-plus/reports`
 * (enhanced Feb 2026 to include a 240-char preview snippet).
 *
 * Every card has: date, first-line hook, "Open" link, and a
 * "Copy share link" affordance so a buyer can email their own
 * report to a family member without touching the checkout again.
 *
 * If the user has zero reports, we show a helpful empty state that
 * points them at the sample + buy card on /plus rather than a bare
 * "nothing to show" screen.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Copy, Check, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PathwaySEO from "@/components/app/PathwaySEO";

function firstLine(preview) {
  if (!preview) return "";
  // Strip a leading "# ..." H1 line — the hook we actually want to
  // show sits on the following non-empty line.
  const lines = preview.split(/\r?\n/).filter((l) => l.trim().length > 0);
  const withoutH1 = lines.filter((l) => !l.startsWith("#"));
  return (withoutH1[0] || lines[0] || "").replace(/^[*_]+|[*_]+$/g, "").slice(0, 200);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso.slice(0, 10); }
}

export default function PlusMyReports() {
  const { user, loading: authLoading, login } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    api.get("/wua-plus/reports")
      .then((r) => setRows(r.data?.reports || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [authLoading, user]);

  const copyLink = async (reportId) => {
    const url = `${window.location.origin}/wua-plus/report/${reportId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(reportId);
      toast.success("Share link copied to clipboard.");
      setTimeout(() => setCopiedId((c) => (c === reportId ? null : c)), 1500);
    } catch { toast.error("Could not copy — long-press the Open button to grab the URL."); }
  };

  return (
    <div data-testid="plus-my-reports-page" className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
      <PathwaySEO
        title="My WUA Personalised Reports"
        description="Your library of past WUA Personalised Reports — every $49 report you've ever generated, kept forever, one click away."
        path="/plus/my-reports"
      />

      <Link
        to="/plus"
        className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#4C1D95]"
        data-testid="plus-my-reports-back"
      >
        <ArrowLeft className="h-3 w-3" /> Back to WUA
      </Link>

      <h1 className="mt-4 font-editorial text-4xl text-[#002147]">My reports</h1>
      <p className="mt-2 text-stone-600 max-w-2xl">
        Every WUA Personalised Report you've ever generated. We keep them
        forever — share the link with a parent, print for a family
        meeting, or reopen when your shortlist changes.
      </p>

      {(!authLoading && !user) && (
        <div className="mt-8 rounded-lg border border-stone-200 bg-stone-50 p-6" data-testid="plus-my-reports-signin-prompt">
          <p className="text-stone-700">
            Please sign in with the Google account you used at checkout to
            see your report history.
          </p>
          <Button
            onClick={login}
            className="mt-3 bg-[#4C1D95] hover:bg-[#5B21B6] text-white"
            data-testid="plus-my-reports-signin-btn"
          >
            Sign in
          </Button>
        </div>
      )}

      {user && loading && (
        <div className="mt-8 text-sm text-stone-500" data-testid="plus-my-reports-loading">
          Loading your reports…
        </div>
      )}

      {user && !loading && rows.length === 0 && (
        <div
          className="mt-8 rounded-xl border border-[#4C1D95]/25 bg-[#F5F0FF] p-8 text-center"
          data-testid="plus-my-reports-empty"
        >
          <Sparkles className="h-8 w-8 text-[#4C1D95] mx-auto" aria-hidden />
          <h2 className="mt-3 font-editorial text-2xl text-[#002147]">
            You haven't generated a report yet
          </h2>
          <p className="mt-2 text-stone-600 max-w-lg mx-auto">
            A WUA Personalised Report turns your shortlist into a five-section
            strategic plan in 60 seconds — $49 one-off, no subscription.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <Link to="/wua-plus/sample">
              <Button variant="outline" data-testid="plus-my-reports-see-sample">
                See a sample
              </Button>
            </Link>
            <Link to="/plus">
              <Button className="bg-[#4C1D95] hover:bg-[#5B21B6] text-white" data-testid="plus-my-reports-buy-cta">
                Get my $49 report <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {user && !loading && rows.length > 0 && (
        <div className="mt-6 space-y-3" data-testid="plus-my-reports-list">
          {rows.map((r) => (
            <div
              key={r.report_id}
              data-testid={`plus-my-reports-row-${r.report_id}`}
              className="rounded-lg border border-stone-200 bg-white p-4 hover:border-[#4C1D95]/40 hover:shadow-sm transition"
            >
              <div className="flex items-start gap-3">
                <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#F5F0FF] text-[#4C1D95]">
                  <FileText className="h-4 w-4" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-editorial text-lg text-[#002147]">
                      Report · {fmtDate(r.generated_at)}
                    </span>
                    <span className="text-[10px] font-underground-sc tracking-wider text-stone-400">
                      #{(r.report_id || "").slice(0, 8)}
                    </span>
                  </div>
                  {firstLine(r.preview) && (
                    <p className="mt-1 text-[13px] text-stone-600 leading-snug line-clamp-2">
                      {firstLine(r.preview)}
                    </p>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    <Link to={`/wua-plus/report/${r.report_id}`}>
                      <Button
                        size="sm"
                        className="bg-[#4C1D95] hover:bg-[#5B21B6] text-white"
                        data-testid={`plus-my-reports-open-${r.report_id}`}
                      >
                        Open <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyLink(r.report_id)}
                      data-testid={`plus-my-reports-copy-${r.report_id}`}
                    >
                      {copiedId === r.report_id ? (
                        <><Check className="h-3 w-3 mr-1" /> Copied</>
                      ) : (
                        <><Copy className="h-3 w-3 mr-1" /> Copy link</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
